/** @jsxImportSource @emotion/react */
import {css} from "@emotion/react";
import {useState, useContext, useEffect, useCallback, useMemo, useRef} from "react";
import moment from "moment";
import {IconButton, Menu, MenuItem} from "@mui/material";
import * as Immutable from "immutable";
import * as ScheduledReports from "js/common/scheduled-reports";
import * as SavedConfigs from "js/common/saved-configs";
import * as Auditor from "js/common/auditor";
import {indexBy} from "js/common/utils/collections";
import {CustomThemeContext} from "js/common/themes/CustomThemeProvider";
import ScheduleDrawer from "js/dashboards/scheduling/schedule-drawer";
import SimpleDataTable from "js/common/views/tables/simple-data-table";
import LoadingSpinner from "js/common/views/loading-spinner";

const formatDateTime = (dateTimeString, pattern = "MMM D, YYYY h:mm A") => {
  if (!dateTimeString) return "-";
  return moment(dateTimeString).format(pattern);
};

const getRecurrenceText = (rruleString) => {
  if (!rruleString) return "One-time";

  const freqMatch = rruleString.match(/FREQ=(\w+)/);

  if (!freqMatch) return "One-time";

  const freq = freqMatch[1];

  const frequencyMap = {
    DAILY: "Daily",
    WEEKLY: "Weekly",
    MONTHLY: "Monthly",
    YEARLY: "Yearly",
    HOURLY: "Hourly"
  };

  return frequencyMap[freq] || "Custom";
};

const getStyles = (theme) => ({
  iconButton: css`
    padding: 0px;
    font-size: 14px;
  `,
  searchContainer: css`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 10px;
    padding: 0;
  `,
  searchInput: css`
    border-radius: 0.2rem;
    border: 1px solid ${theme.palette.border.main};
    font-size: 0.85rem;
    color: ${theme.palette.text.main};
    background-color: ${theme.palette.background.card};
    padding: 7px 12px;
    width: 15%;
    min-width: 340px;
    font-family: ${theme.typography.fontFamily};
    display: flex;
    align-items: center;

    input {
      font-size: 14px;
      border: none;
      width: 100%;
      padding-left: 10px;
      background-color: ${theme.palette.background.card};
      color: ${theme.palette.text.main};
      font-family: ${theme.typography.fontFamily};
      outline: none;
    }
  `
});

function ActionMenu({rowId, onEdit}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const {theme} = useContext(CustomThemeContext);
  const open = Boolean(anchorEl);
  const styles = getStyles(theme);

  const handleClick = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleEdit = () => {
    onEdit(rowId);
    handleClose();
  };

  return (
    <>
      <IconButton onClick={handleClick} size="small">
        <i className="bhi-kebab-menu" css={styles.iconButton} />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right"
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right"
        }}
      >
        <MenuItem sx={{fontSize: 13, padding: "0 0.5rem"}} onClick={handleEdit}>
          Edit
        </MenuItem>
      </Menu>
    </>
  );
}

function ScheduledReportsTable() {
  const [idToScheduledReport, setIdToScheduledReport] = useState(Immutable.Map());
  const [dashboards, setDashboards] = useState(Immutable.List());
  const [scheduleConfigId, setScheduleConfigId] = useState(null);
  const [scheduleIdToEdit, setScheduleIdToEdit] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [addNewMode, setAddNewMode] = useState(false);
  const [containerHeight, setContainerHeight] = useState(600);
  const [isLoading, setIsLoading] = useState(true);

  const scheduleContainerRef = useRef(null);

  const idToDashboard = useMemo(() => indexBy((dashboard) => dashboard.get("id"), dashboards), [dashboards]);

  const {theme} = useContext(CustomThemeContext);
  const styles = getStyles(theme);

  useEffect(() => {
    Promise.all([
      ScheduledReports.getAllDashboardsForScheduledReportAdmin(),
      ScheduledReports.getAllScheduledReportsForAdmin()
    ]).then(([dashboards, scheduledReports]) => {
      setDashboards(dashboards)
      setIdToScheduledReport(indexBy((report) => report.get("id"), scheduledReports))
      setIsLoading(false);
    });
    Auditor.audit("scheduled-reports-admin:loaded");
  }, []);

  useEffect(() => {
    const updateContainerHeight = () => {
      if (scheduleContainerRef.current) {
        setContainerHeight(scheduleContainerRef.current.clientHeight);
      }
    };

    updateContainerHeight();
    window.addEventListener("resize", updateContainerHeight);

    return () => {
      window.removeEventListener("resize", updateContainerHeight);
    };
  }, []);

  const dashboardNameById = (id) => {
    const dashboard = idToDashboard.get(id);
    return dashboard ? dashboard.get("name") : "Unknown Dashboard";
  };

  const handleEditSchedule = useCallback(
    (scheduleId) => {
      const schedule = idToScheduledReport.get(scheduleId);
      if (schedule) {
        setScheduleConfigId(schedule.get("dashboardId"));
        setScheduleIdToEdit(scheduleId);
        setAddNewMode(false);
      }
    },
    [idToScheduledReport]
  );

  const closeScheduleDrawer = useCallback(() => {
    setScheduleConfigId(null);
    setScheduleIdToEdit(null);
    setAddNewMode(false);
  }, []);

  const handleScheduleUpdate = useCallback((schedule) => {
    setIdToScheduledReport(idToScheduledReport => idToScheduledReport.set(schedule.get("id"), schedule));
  }, []);

  const onCellClick = useCallback(
    (cellValue, rowArray, columnIndex, rowIndex) => {
      const actionsColumnIndex = rowArray.findIndex((cell) => cell.columnName === "actions");
      const reportId = rowArray[actionsColumnIndex]?.originalValue;
      if (columnIndex !== actionsColumnIndex && reportId) {
        handleEditSchedule(reportId);
      }
    },
    [handleEditSchedule]
  );

  const columns = [
    {
      label: "Name",
      flexGrow: 2,
      titleMapper: (value) => value
    },
    {label: "Report", flexGrow: 2},
    {label: "Status", flexGrow: 1},
    {label: "Recurring Schedule", flexGrow: 1},
    {
      label: "Created Date",
      flexGrow: 1,
      displayMapper: (value) => formatDateTime(value),
      sortMapper: (value) => (value ? new Date(value).getTime() : 0)
    },
    {
      label: "Last Modified",
      flexGrow: 1,
      displayMapper: (value) => formatDateTime(value),
      sortMapper: (value) => (value ? new Date(value).getTime() : 0)
    },
    {
      label: "Last Sent",
      flexGrow: 1,
      displayMapper: (value) => value === "FAILED" ? "Failed" : formatDateTime(value),
      sortMapper: (value) => value === "FAILED" ? 0 : new Date(value).getTime()
    },
    {
      label: "Actions",
      flexGrow: 0,
      width: 60,
      disableSort: true,
      displayMapper: (value, originalValue) => {
        return <ActionMenu rowId={originalValue} onEdit={handleEditSchedule} />;
      }
    }
  ];

  const reportToRow = (report) => {
    const lastRunDate = report.get("lastRunDateTimeUtc");
    const lastStatus = report.get("lastRunStatus");
    const lastSent = lastStatus === "FAILED" ? "FAILED" : lastRunDate;
    return [
      report.get("name"),
      dashboardNameById(report.get("dashboardId")),
      "Active",
      getRecurrenceText(report.get("recurrenceSchedule")),
      report.get("createdDateTimeUtc"),
      report.get("updatedDateTimeUtc"),
      lastSent,
      report.get("id")
    ];
  };

  const filteredReports = idToScheduledReport.valueSeq().filter((report) => {
    if (!searchText) return true;
    const searchLower = searchText.toLowerCase();
    const name = report.get("name", "").toLowerCase();
    const dashboardName = dashboardNameById(report.get("dashboardId")).toLowerCase();
    return name.includes(searchLower) || dashboardName.includes(searchLower);
  });

  const rows = filteredReports.map(reportToRow).toJS();

  return (
    <>
      <div ref={scheduleContainerRef} style={{maxWidth: "100%", margin: "0 auto", height: "calc(100vh - 170px)"}}>
        {isLoading ? (
          <LoadingSpinner />
        ) : (
          <>
            <div css={styles.searchContainer}>
              <div css={styles.searchInput}>
                <i className="bhi-search" />
                <input value={searchText} placeholder="Search..." onChange={(e) => setSearchText(e.target.value)} />
              </div>
            </div>

            <SimpleDataTable
              id="scheduled-reports-table"
              columns={columns}
              rows={rows}
              onCellClick={onCellClick}
              initialSortDirection="ASC"
              initialSortBy={0}
              maxTableHeight={containerHeight}
              downloadable={false}
              searchable={false}
            />
          </>
        )}
      </div>

      {!dashboards.isEmpty() && (
        <ScheduleDrawer
          open={scheduleConfigId !== null}
          onRequestClose={closeScheduleDrawer}
          configId={scheduleConfigId}
          scheduleToEdit={scheduleIdToEdit ? idToScheduledReport.get(scheduleIdToEdit) : null}
          idToDashboard={idToDashboard}
          SavedConfigs={SavedConfigs}
          onScheduleUpdate={handleScheduleUpdate}
          addNewMode={addNewMode}
          existingSchedules={idToScheduledReport.valueSeq()}
        />
      )}
    </>
  );
}

export default ScheduledReportsTable;
